/**
 * Onboarding's own renders of a Doc frame, chosen by its
 * `metadata.display_mode`: founder_card, research_summary.
 * Mounted by doc-frame.tsx instead of the editor.
 */
import type { FrameData } from "@/lib/frame-data";
import { useT } from "@/i18n";
import { framesNoteMessages } from "@/i18n/messages/frames-note";

/* ---------- Types ---------- */

interface FounderCardContent {
  headshot_url?: string | null;
  name?: string | null;
  business_name?: string | null;
  business_url?: string | null;
  headline?: string | null;
  sub_headline?: string | null;
  manifesto?: string | null;
}

interface OnboardingNoteRenderProps {
  frame: FrameData;
}

/* ---------- FounderCardRender ---------- */

export function FounderCardRender({ frame }: OnboardingNoteRenderProps) {
  const t = useT(framesNoteMessages);
  const content = (frame.content ?? {}) as FounderCardContent;

  const initials = (() => {
    const name = content.name ?? "";
    return name
      .split(" ")
      .slice(0, 2)
      .map((w) => w[0] ?? "")
      .join("")
      .toUpperCase();
  })();

  return (
    <div
      id={`founder-card-render-${frame.frameId}`}
      data-testid="founder-card"
      className="relative flex flex-col items-center min-w-80 max-w-96 rounded-frame bg-surface p-6 aspect-[4/5]"
    >
      {/* Avatar */}
      <div className="relative flex items-center justify-center mb-4">
        <svg width="120" height="120" className="absolute">
          <circle
            cx="60"
            cy="60"
            r="56"
            fill="none"
            stroke="var(--color-accent)"
            strokeWidth="3"
          />
        </svg>
        {content.headshot_url ? (
          <img
            src={content.headshot_url}
            alt={content.name ?? t("founderPhotoAlt")}
            className="w-24 h-24 rounded-full object-cover"
          />
        ) : (
          <div className="w-24 h-24 rounded-full flex items-center justify-center text-2xl font-bold text-accent">
            {initials || "?"}
          </div>
        )}
      </div>

      {/* Identity */}
      <div className="text-center space-y-0.5 w-full">
        {content.name && (
          <div className="text-xl font-bold text-primary truncate">
            {content.name}
          </div>
        )}
        {content.business_name && (
          <div className="text-base text-secondary truncate">
            {content.business_name}
          </div>
        )}
        {content.business_url && (
          <div className="text-sm text-muted truncate">
            {content.business_url}
          </div>
        )}
      </div>

      {/* Headlines */}
      {(content.headline || content.sub_headline) && (
        <div className="mt-3 text-center w-full space-y-1">
          {content.headline && (
            <div className="text-sm font-medium text-primary">
              {content.headline}
            </div>
          )}
          {content.sub_headline && (
            <div className="text-xs text-muted">{content.sub_headline}</div>
          )}
        </div>
      )}

      {/* Manifesto */}
      <div className="mt-4 w-full flex-1">
        {content.manifesto ? (
          <p className="text-xs text-secondary leading-relaxed">
            {content.manifesto}
          </p>
        ) : (
          <div className="space-y-2">
            <div className="h-3 rounded-control-sm bg-glass-fill animate-pulse" />
            <div className="h-3 rounded-control-sm bg-glass-fill animate-pulse w-4/5" />
            <div className="h-3 rounded-control-sm bg-glass-fill animate-pulse w-3/5" />
          </div>
        )}
      </div>

      {/* Watermark */}
      <span className="absolute bottom-2 right-3 text-appkit-mini text-muted">
        {/* i18n-ignore: the domain name reads the same in every language */}
        denker.ai
      </span>
    </div>
  );
}

/* ---------- ResearchSummaryRender ---------- */

export function ResearchSummaryRender({ frame }: OnboardingNoteRenderProps) {
  const bodyText =
    typeof (frame.content as Record<string, unknown>)?.text === "string"
      ? ((frame.content as Record<string, unknown>).text as string)
      : "";
  const hasUrl =
    frame.title?.toLowerCase().includes("url") ||
    frame.title?.toLowerCase().includes("handle") ||
    bodyText.includes("http");

  return (
    <div
      data-testid="research-summary"
      className="flex h-full w-full flex-col overflow-hidden rounded-control-sm bg-glass-fill-heavy"
    >
      <div
        className="scrollbar-none min-h-0 flex-1 overflow-auto p-2"
        data-frame-content="true"
      >
        {frame.title && (
          <div className="mb-1 truncate text-xs font-semibold text-secondary">
            {frame.title}
          </div>
        )}
        <div
          className={`whitespace-pre-wrap break-all text-xs text-primary ${hasUrl ? "font-mono" : ""}`}
        >
          {bodyText}
        </div>
      </div>
    </div>
  );
}
